/**
 * <OverrideStackViewer /> — visualizes one component's 8-layer
 * override resolution path (M12.4, F4-refactored to design tokens).
 *
 * Per DESIGN_SYSTEM.md §6 "The trace-on-click pattern": this is the
 * canonical drawer body for any traceable value. Opens with the
 * full resolution path; the winning layer is highlighted, layers
 * above are dimmed (no value to override), layers below are struck
 * through (overridden by the winner).
 *
 * F4 refactor: replaced hardcoded emerald/rose/slate utility classes
 * with design-system tokens (bg-surface-*, text-text-*, bg-state-*).
 * Theme + future-dark-mode inherit automatically.
 */

import { Ban, Check, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ResolutionLayerEntry } from "../lib/types";

// Stop-toggle sentinel from M12 — when a stop_toggle layer wins,
// the value is a marker string we render as BLOCKED rather than raw.
const STOP_ACCRUAL_SENTINEL = "__STOP_ACCRUAL__";

const LAYER_KEYS: Record<string, string> = {
  stop_toggle: "app.osv_layer_stop_toggle",
  contract_override: "app.osv_layer_contract_override",
  position: "app.osv_layer_position",
  job: "app.osv_layer_job",
  grade: "app.osv_layer_grade",
  legal_entity: "app.osv_layer_legal_entity",
  customer_group: "app.osv_layer_customer_group",
  ksa_statutory: "app.osv_layer_ksa_statutory",
};

function useFormatLayerLabel(): (layer: string) => string {
  const { t } = useTranslation();
  return (layer: string) => {
    if (layer.startsWith("stop_toggle")) {
      const which = layer.includes(":") ? layer.split(":")[1] : "";
      return which
        ? t("app.osv_layer_stop_toggle_named", { which })
        : t("app.osv_layer_stop_toggle");
    }
    const k = LAYER_KEYS[layer];
    return k ? t(k) : layer;
  };
}

function useFormatValue(): (v: unknown) => string {
  const { t } = useTranslation();
  return (v: unknown) => {
    if (v === null || v === undefined) return "—";
    if (v === STOP_ACCRUAL_SENTINEL) return t("app.osv_blocked_value");
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return String(v);
    return JSON.stringify(v);
  };
}

function isPopulated(v: unknown): boolean {
  return v !== null && v !== undefined;
}

export function OverrideStackViewer({
  componentName,
  path,
}: {
  componentName: string;
  path: ResolutionLayerEntry[];
}) {
  const { t } = useTranslation();
  const formatLayerLabel = useFormatLayerLabel();
  const formatValue = useFormatValue();
  // The winner is the FIRST layer (top-down) whose value is populated.
  const winnerIndex = path.findIndex((p) => isPopulated(p.value));

  return (
    <div className="rounded-md border border-border-subtle bg-surface-default p-3">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-text-tertiary">
        <Layers className="h-3.5 w-3.5" />
        {t("app.osv_stack_header", { component: componentName })}
      </div>
      <ol className="space-y-1.5">
        {path.map((entry, i) => {
          const populated = isPopulated(entry.value);
          const isWinner = i === winnerIndex;
          const overridden =
            winnerIndex !== -1 && i > winnerIndex && populated;
          const isStopBlock =
            isWinner && entry.value === STOP_ACCRUAL_SENTINEL;

          // Token-driven row styling — winner uses approved tone for
          // a positive computed value, denied tone when the result
          // is a BLOCKED stop-toggle, otherwise default surface.
          const rowClasses = [
            "flex items-center gap-2 rounded-sm border px-2 py-1 text-xs",
            isWinner
              ? isStopBlock
                ? "border-state-denied-bg bg-state-denied-bg"
                : "border-state-approved-bg bg-state-approved-bg"
              : overridden
              ? "border-border-subtle bg-surface-sunken"
              : "border-border-subtle bg-surface-default",
          ].join(" ");

          return (
            <li key={`${entry.layer}-${i}`} className={rowClasses}>
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                {isWinner ? (
                  isStopBlock ? (
                    <Ban className="h-4 w-4 text-state-denied" />
                  ) : (
                    <Check className="h-4 w-4 text-state-approved" />
                  )
                ) : (
                  <span className="text-text-tertiary">·</span>
                )}
              </span>
              <span
                className={[
                  "w-40 font-medium",
                  populated ? "text-text-primary" : "text-text-tertiary",
                ].join(" ")}
              >
                {formatLayerLabel(entry.layer)}
              </span>
              <span
                className={[
                  "flex-1 font-mono tabular-nums",
                  overridden
                    ? "text-text-tertiary line-through"
                    : populated
                    ? "text-text-primary"
                    : "text-text-tertiary",
                ].join(" ")}
              >
                {formatValue(entry.value)}
              </span>
              {entry.source_id && (
                <span className="hidden font-mono text-xs text-text-tertiary md:inline">
                  {entry.source_id.slice(0, 8)}…
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {winnerIndex === -1 && (
        <p className="mt-2 text-xs italic text-text-tertiary">
          {t("app.osv_no_layer")}
        </p>
      )}
    </div>
  );
}
